// Yelp photos load straight from Yelp's CDN, which allows cross-site loading and tells
// browsers to cache them for years. Going through our API (the old /gcm/image proxy) cost
// one API request per photo per page view, counted against the API rate limit, and saved
// every photo to disk. Non-Yelp URLs (GCM placeholders) give null so callers show the logo.
// Use with referrerPolicy="no-referrer" in case Yelp ever starts checking referers.
export const gcmPhotoUrl = (url) => (isYelpPhoto(url) ? url : null);

// The proxy only fetches from Yelp's CDN. Merchants without a Yelp photo come back
// with a GCM placeholder URL instead, which the proxy refuses and which doesn't load
// directly either, so those are treated as having no photo.
export const isYelpPhoto = (url) => {
    try {
        return new URL(url).hostname.endsWith('.yelpcdn.com');
    } catch (_) {
        return false;
    }
};

export const searchPath = (query, location) =>
    `/participant/gcm-search?q=${encodeURIComponent(query)}&loc=${encodeURIComponent(location)}`;

// The product page reads the business back from sessionStorage (see getMerchant),
// which saves a GCM lookup when arriving from a list
export const openBusiness = (navigate, business) => {
    sessionStorage.setItem(`gcm_business_${business.id}`, JSON.stringify(business));
    navigate(`/participant/redeem/gcm-${business.id}`, { state: business });
};

// The business's Yelp page. The payload doesn't carry Yelp's URL yet; yelp.com/biz/<id>
// redirects to the business page, so build it from the id until a `url` field is added.
export const yelpBusinessUrl = (business) =>
    business?.url || (business?.id ? `https://www.yelp.com/biz/${encodeURIComponent(business.id)}` : null);

// Whether a cart holds any GCM items (they bring in the Gift Card Market logo)
export const hasGcmItems = (items) =>
    (items || []).some((item) => String(item.merchant_id).startsWith('gcm-'));

// The business for a GCM cart item, for <GcmCartPhoto> in the cart/checkout tables.
// Items added before rating was stored fall back to the business cached when it was viewed.
export const gcmCartBusiness = (item, merchant = {}) => {
    const id = String(item.merchant_id).replace(/^gcm-/, '');
    let cached = {};
    try { cached = JSON.parse(sessionStorage.getItem(`gcm_business_${id}`)) || {}; } catch (_) {}
    return {
        id,
        name: item.merchant_name,
        image_url: item.merchant_image_url || merchant.image_url || cached.image_url,
        rating: item.merchant_rating ?? merchant.rating ?? cached.rating,
        review_count: item.merchant_review_count ?? merchant.review_count ?? cached.review_count,
    };
};

// The business for a GCM gift code on My Gift Codes. The gift-codes payload carries the
// name, a proxy URL for the photo (merchant.icon) and the Yelp id and rating (gcm_id,
// rating, review_count); older backends without those fall back to a business cached
// this session with the same photo.
export const gcmGiftCodeBusiness = (giftCode) => {
    const merchant = giftCode?.merchant || {};
    let imageUrl = null;
    try {
        imageUrl = new URL(merchant.icon || '', window.location.origin).searchParams.get('url');
    } catch (_) {}

    let cached = null;
    try {
        if (merchant.gcm_id) {
            cached = JSON.parse(sessionStorage.getItem(`gcm_business_${merchant.gcm_id}`));
        } else if (imageUrl) {
            for (let i = 0; i < sessionStorage.length && !cached; i++) {
                const key = sessionStorage.key(i);
                if (!key.startsWith('gcm_business_')) continue;
                const business = JSON.parse(sessionStorage.getItem(key));
                if (business?.image_url === imageUrl) cached = business;
            }
        }
    } catch (_) {}

    return {
        id: merchant.gcm_id || cached?.id || null,
        name: merchant.name,
        image_url: imageUrl || cached?.image_url,
        rating: merchant.rating ?? cached?.rating,
        review_count: merchant.review_count ?? cached?.review_count,
    };
};


// Yelp phones come as E.164 ("+12125107714"); show US numbers as "(212) 510-7714"
export const formatPhone = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    const us = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    return us.length === 10 ? `(${us.slice(0, 3)}) ${us.slice(3, 6)}-${us.slice(6)}` : (phone || '');
};

// Rich-text fields save an emptied editor as markup like "<p>&nbsp;<br></p>"; treat that as unset
export const htmlHasText = (html) =>
    Boolean(String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, ''));

// The GCM description and redemption instructions are stored once for all GCM cards (on the GCM
// parent merchant) with placeholders filled from the card's business: {{merchant}} (name),
// {{phone}}, {{category}} (Yelp's first category, e.g. "Steakhouses") and
// {{payload.description}} (GCM's own description of the business, often empty). Values are
// HTML-escaped since the text renders as HTML.
const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

export const fillGcmPlaceholders = (html, business = {}) => {
    if (!html) return html;
    const values = {
        merchant: business.name,
        phone: formatPhone(business.phone),
        category: business.categories?.[0]?.title,
        'payload.description': business.payload_description,
    };
    return html.replace(/\{\{\s*(merchant|phone|category|payload\.description)\s*\}\}/gi, (_, key) => escapeHtml(values[key.toLowerCase()]));
};
